import { Fragment, useState } from 'react';

import { pluriel } from '@/lib/pluriel';
import { CancelOrderModal } from '@/components/admin/cancel-order-modal';
import { useNavigate, useParams } from 'react-router-dom';

import { AdminAvatar } from '@/components/admin/avatar';
import {
  ConfirmBankTransferModal,
  ExtendReservationModal,
} from '@/components/admin/bank-transfer-modals';
import { AdminButton } from '@/components/admin/button';
import { Card, CardBody, CardHead } from '@/components/admin/card';
import { AdminIcon } from '@/components/admin/icon';
import { Tag } from '@/components/admin/tag';
import { useToast } from '@/components/ui/feedback';
import { useAdminUser } from '@/hooks/use-admin-users';
import { fmtFCFA } from '@/lib/format';
import { estTropProlongee, etatReservation } from '@/lib/orders/bank-transfer';
import {
  customerKindLabel,
  DELIVERY_MODE_LABEL,
  EVENT_COLOR_CSS,
  hasNotificationDetails,
  NOTIFICATION_CHANNEL_LABEL,
  NOTIFICATION_RESULT_LABEL,
  ORDER_FLAG_LABEL,
  ORDER_SOURCE_LABEL,
  ORDER_STATUS_SUB_LABEL,
  orderEventColor,
  orderEventLabel,
  PAYMENT_METHOD_LABEL,
  PAYMENT_STATUS_LABEL,
  PICKUP_STATUS_LABEL,
  plural,
  readNotificationDetails,
  resolveReasonLabel,
} from '@/lib/orders/labels';
import { ordersApi } from '@/lib/orders/orders-api';
import {
  useAddOrderNote,
  useCancelOrder,
  useOrder,
  useOrderEvents,
  useOrderNotes,
  useSetOrderStatus,
} from '@/lib/orders/orders-hooks';
import type { OrderStatus } from '@/lib/orders/types';
import { statusOrderV2 } from '@/lib/status';

const PAYMENT_STATUS_CLS: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  UNPAID: 'warning',
  AUTHORIZED: 'info',
  CAPTURED: 'info',
  REFUNDED: 'warning',
  FAILED: 'danger',
};

const ALLOWED_NEXT: Record<OrderStatus, OrderStatus[]> = {
  DRAFT: ['PENDING_PAYMENT', 'CANCELLED'],
  PENDING_PAYMENT: ['PAID', 'CANCELLED'],
  PAID: ['CONFIRMED', 'CANCELLED'],
  CONFIRMED: ['PROCURING', 'CANCELLED', 'DISPUTED'],
  PROCURING: ['IN_HUB', 'CANCELLED', 'DISPUTED'],
  IN_HUB: ['READY_TO_SHIP', 'CANCELLED', 'DISPUTED'],
  READY_TO_SHIP: ['SHIPPING', 'CANCELLED', 'DISPUTED'],
  SHIPPING: ['DELIVERED', 'DISPUTED'],
  DELIVERED: ['COMPLETED', 'DISPUTED'],
  COMPLETED: ['REFUNDED', 'DISPUTED'],
  CANCELLED: [],
  REFUNDED: [],
  DISPUTED: ['REFUNDED', 'COMPLETED', 'CANCELLED'],
};

/** Workflow visuel — 9 étapes du cycle de vie nominal d'une commande. */
const WORKFLOW_STEPS: Array<{ status: OrderStatus; label: string }> = [
  { status: 'PENDING_PAYMENT', label: 'Commande créée' },
  { status: 'PAID', label: 'Paiement validé' },
  { status: 'CONFIRMED', label: 'Stock réservé' },
  { status: 'PROCURING', label: 'Approvisionnement' },
  { status: 'IN_HUB', label: 'Réceptionnée au hub' },
  { status: 'READY_TO_SHIP', label: 'Prête à expédier' },
  { status: 'SHIPPING', label: 'En tournée livraison' },
  { status: 'DELIVERED', label: 'Livrée au client' },
  { status: 'COMPLETED', label: 'Clôturée' },
];

function getStepState(stepIdx: number, currentIdx: number): 'done' | 'active' | 'pending' {
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) return 'active';
  return 'pending';
}

interface PaymentBadgeProps {
  method: string;
}

function PaymentMethodBadge({ method }: PaymentBadgeProps) {
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    MOMO: { bg: '#FFCC00', fg: '#000', label: 'MoMo' },
    OM: { bg: '#FF6600', fg: '#fff', label: 'OM' },
    BANK_TRANSFER: { bg: '#1e4d3b', fg: '#fff', label: 'BANK' },
    COD: { bg: '#7d6149', fg: '#fff', label: 'COD' },
    CARD: { bg: '#1a1f71', fg: '#fff', label: 'CARD' },
  };
  const cfg = map[method] ?? {
    bg: '#666',
    fg: '#fff',
    label: method.slice(0, 4),
  };
  return (
    <div
      style={{
        width: 40,
        height: 26,
        borderRadius: 4,
        background: cfg.bg,
        color: cfg.fg,
        display: 'grid',
        placeItems: 'center',
        fontSize: 9,
        fontWeight: 900,
        letterSpacing: 0.5,
        flexShrink: 0,
      }}
    >
      {cfg.label}
    </div>
  );
}

/**
 * Identifiant fiscal affiché avec sa copie en un clic.
 *
 * Un NUI ou un RCCM se recopie dans une facture ou un mail de relance : le
 * retaper de mémoire à partir de l'écran produit des factures rejetées par la
 * comptabilité du client.
 */
function IdentifiantCopiable({ label, valeur }: { label: string; valeur: string }) {
  const [copie, setCopie] = useState(false);
  const copier = () => {
    void navigator.clipboard.writeText(valeur);
    setCopie(true);
    setTimeout(() => setCopie(false), 2000);
  };
  return (
    <div className="info-row">
      <span className="l">{label}</span>
      <span className="v" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span className="mono" style={{ fontSize: 11 }}>
          {valeur}
        </span>
        <button
          type="button"
          onClick={copier}
          aria-label={`Copier le ${label}`}
          title={copie ? 'Copié !' : `Copier le ${label}`}
          className="active:scale-[0.96]"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 2,
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <AdminIcon
            n={copie ? 'check' : 'tag'}
            s={12}
            c={copie ? 'var(--a-success)' : 'var(--a-ink-mute)'}
          />
        </button>
      </span>
    </div>
  );
}

export function AdminOrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrder(id);
  const { data: events } = useOrderEvents(id);
  const { data: notes } = useOrderNotes(id);
  const { data: operator } = useAdminUser(order?.assignedOperatorId ?? undefined);
  const setStatus = useSetOrderStatus(id ?? '');
  const cancelMut = useCancelOrder(id ?? '');
  const addNote = useAddOrderNote(id ?? '');
  const [noteDraft, setNoteDraft] = useState('');
  const [statusError, setStatusError] = useState<string | null>(null);
  const [showJournal, setShowJournal] = useState(false);
  const [virementModal, setVirementModal] = useState<'confirmer' | 'prolonger' | null>(null);
  const [annulationOuverte, setAnnulationOuverte] = useState(false);
  // P18 : lignes pack dépliables (composants snapshotés).
  const [expandedLines, setExpandedLines] = useState<Set<string>>(new Set());
  const toast = useToast();

  if (isLoading) {
    return (
      <div className="empty" style={{ padding: 40 }}>
        Chargement…
      </div>
    );
  }
  if (!order) {
    return (
      <div className="empty" style={{ padding: 40 }}>
        Commande introuvable.
      </div>
    );
  }

  const s = statusOrderV2(order.status);
  const toggleLine = (id: string) =>
    setExpandedLines((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const totalItems = (order.lines ?? []).reduce((sum, l) => sum + l.quantity, 0);
  const nextOptions = ALLOWED_NEXT[order.status] ?? [];
  const currentStepIdx = WORKFLOW_STEPS.findIndex((step) => step.status === order.status);
  const isTerminal =
    order.status === 'CANCELLED' || order.status === 'REFUNDED' || order.status === 'DISPUTED';

  const onAdvance = async () => {
    const target = nextOptions.find((s) => s !== 'CANCELLED' && s !== 'DISPUTED');
    if (!target) return;
    try {
      setStatusError(null);
      await setStatus.mutateAsync({ status: target });
    } catch (e: unknown) {
      setStatusError((e as Error).message);
    }
  };

  // La fenêtre système ne montrait ni le montant en jeu, ni l'avertissement sur
  // la validation par un second responsable : le refus du serveur arrivait donc
  // sans explication, et l'opérateur croyait la commande annulée. Le motif est
  // désormais choisi dans une liste, et l'erreur remontée dans la fenêtre.
  const onCancel = async (reasonCode: string) => {
    await cancelMut.mutateAsync({ reasonCode });
    toast.success(`Commande ${order.number} annulée.`);
  };

  const onAddNote = async () => {
    if (!noteDraft.trim()) return;
    await addNote.mutateAsync(noteDraft.trim());
    setNoteDraft('');
  };

  const customerName = order.customer
    ? `${order.customer.firstName} ${order.customer.lastName}`
    : 'Client';
  const operatorName = operator ? `${operator.firstName} ${operator.lastName}` : null;

  return (
    <>
      {/* Crumbs */}
      <div className="crumbs" style={{ marginBottom: 14, fontSize: 12 }}>
        <a className="root" style={{ cursor: 'pointer' }} onClick={() => navigate('/commandes')}>
          Commandes
        </a>
        <span className="sep">
          <AdminIcon n="chevR" s={10} />
        </span>
        <span className="here">{order.number}</span>
      </div>

      {/* Header */}
      <div className="page-head" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 6,
              flexWrap: 'wrap',
            }}
          >
            <AdminButton variant="ghost" iconOnly onClick={() => navigate('/commandes')}>
              <AdminIcon n="chevL" s={14} />
            </AdminButton>
            <h1 className="page-title" style={{ margin: 0 }}>
              Commande{' '}
              <span
                className="mono"
                style={{
                  fontWeight: 500,
                  color: 'var(--a-ink-soft)',
                  fontSize: 18,
                }}
              >
                {order.number}
              </span>
            </h1>
            <Tag variant={s.cls} data-testid="order-status-tag">
              {s.l}
            </Tag>
            {order.flags.vip && (
              <Tag variant="terra" noDot title={ORDER_FLAG_LABEL.vip}>
                VIP
              </Tag>
            )}
            {order.flags.first_order && (
              <Tag variant="info" noDot title={ORDER_FLAG_LABEL.first_order}>
                1<sup>re</sup> commande
              </Tag>
            )}
            {order.flags.fraud_suspected && (
              <Tag variant="danger" noDot title={ORDER_FLAG_LABEL.fraud_suspected}>
                Suspicion de fraude
              </Tag>
            )}
            {order.flags.urgent && (
              <Tag variant="warning" noDot title={ORDER_FLAG_LABEL.urgent}>
                Urgent
              </Tag>
            )}
            {order.flags.heavy && (
              <Tag variant="neutral" noDot>
                Volumineux
              </Tag>
            )}
            {order.flags.grouped_delivery && (
              <Tag variant="neutral" noDot>
                Livraison groupée
              </Tag>
            )}
          </div>
          <p className="page-sub">
            Passée le {new Date(order.createdAt).toLocaleString('fr-FR')} ·{' '}
            <strong>{ORDER_STATUS_SUB_LABEL[order.status]}</strong>
            {order.hub && (
              <>
                {' à '}
                <strong>{order.hub.name}</strong>
              </>
            )}
            {' · Source '}
            {ORDER_SOURCE_LABEL[order.source]}
          </p>
        </div>
        <div className="page-actions" style={{ flexWrap: 'wrap' }}>
          <AdminButton
            variant="ghost"
            onClick={() => ordersApi.openPdf(order.id, 'waybill')}
            data-testid="open-waybill"
          >
            <AdminIcon n="print" s={14} />
            Bordereau
          </AdminButton>
          <AdminButton
            variant="ghost"
            onClick={() => ordersApi.openPdf(order.id, 'invoice')}
            data-testid="open-invoice"
          >
            <AdminIcon n="download" s={14} />
            Facture PDF
          </AdminButton>
          {order.customer?.phone && (
            <AdminButton
              variant="ghost"
              onClick={() =>
                window.open(
                  `https://wa.me/${order.customer!.phone!.replace(/[^\d+]/g, '')}`,
                  '_blank',
                )
              }
            >
              <AdminIcon n="chat" s={14} />
              Contacter
            </AdminButton>
          )}
          {nextOptions.length > 0 && (
            <AdminButton variant="primary" onClick={onAdvance} data-testid="advance-status">
              <AdminIcon n="check" s={14} />
              Avancer ({statusOrderV2(nextOptions[0]).l})
            </AdminButton>
          )}
        </div>
      </div>

      {statusError && (
        <div
          style={{
            background: '#fee',
            border: '1px solid #f99',
            padding: 10,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          Erreur : {statusError}
        </div>
      )}

      {/* Detail grid */}
      <div className="detail">
        <div className="stack" style={{ display: 'grid', gap: 16, minWidth: 0 }}>
          {/* Articles */}
          <Card>
            <CardHead
              title={`Articles · ${plural(totalItems, 'unité')}`}
              sub={pluriel((order.lines ?? []).length, 'ligne')}
            />
            <div className="table-wrap" style={{ margin: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th className="right">PU</th>
                    <th className="right">Qté</th>
                    <th>Appro.</th>
                    <th className="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.lines ?? []).map((line) => (
                    <Fragment key={line.id}>
                      <tr>
                        <td>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                            }}
                          >
                            {(line.components?.length ?? 0) > 0 && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-icon"
                                onClick={() => toggleLine(line.id)}
                                title="Voir les composants du pack"
                                data-testid={`pack-toggle-${line.id}`}
                              >
                                <AdminIcon
                                  n={expandedLines.has(line.id) ? 'chevD' : 'chevR'}
                                  s={12}
                                />
                              </button>
                            )}
                            {line.imageSnapshotUrl ? (
                              <img
                                src={line.imageSnapshotUrl}
                                alt={line.nameSnapshot}
                                style={{
                                  width: 44,
                                  height: 44,
                                  objectFit: 'cover',
                                  borderRadius: 6,
                                  border: '1px solid var(--a-border)',
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 44,
                                  height: 44,
                                  borderRadius: 6,
                                  background: 'var(--a-surface-2)',
                                  border: '1px solid var(--a-border)',
                                  display: 'grid',
                                  placeItems: 'center',
                                  color: 'var(--a-ink-faint)',
                                  fontSize: 18,
                                  flexShrink: 0,
                                }}
                              >
                                📦
                              </div>
                            )}
                            <div style={{ minWidth: 0 }}>
                              <strong style={{ fontSize: 13, display: 'block' }}>
                                {line.nameSnapshot}
                                {(line.components?.length ?? 0) > 0 && (
                                  <>
                                    {' '}
                                    <Tag variant="terra" noDot>
                                      Pack · {pluriel(line.components?.length ?? 0, 'article')}
                                    </Tag>
                                  </>
                                )}
                              </strong>
                              <div className="muted" style={{ fontSize: 11 }}>
                                <span className="mono">{line.skuSnapshot}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="right tn">{fmtFCFA(line.unitPrice)}</td>
                        <td className="right tn">{line.quantity}</td>
                        <td>
                          <Tag
                            variant={
                              line.pickupStatus === 'IN_HUB'
                                ? 'success'
                                : line.pickupStatus === 'PICKED_UP'
                                  ? 'info'
                                  : line.pickupStatus === 'SCHEDULED'
                                    ? 'warning'
                                    : 'neutral'
                            }
                            noDot
                          >
                            {PICKUP_STATUS_LABEL[line.pickupStatus]}
                          </Tag>
                        </td>
                        <td className="right tn">
                          <strong>{fmtFCFA(line.lineTotal)}</strong>
                          {line.discountAmount > 0 && (
                            <div className="muted" style={{ fontSize: 11 }}>
                              remise {fmtFCFA(line.discountAmount)}
                            </div>
                          )}
                        </td>
                      </tr>
                      {expandedLines.has(line.id) && (line.components?.length ?? 0) > 0 && (
                        <tr>
                          <td
                            colSpan={5}
                            style={{
                              background: 'var(--a-surface-2)',
                              padding: '6px 18px 10px',
                            }}
                          >
                            <div className="eyebrow" style={{ margin: '6px 0 4px' }}>
                              Contenu du pack (figé à la commande)
                            </div>
                            <table className="table" style={{ background: 'transparent' }}>
                              <thead>
                                <tr>
                                  <th>Article</th>
                                  <th>SKU</th>
                                  <th className="right">Qté / pack</th>
                                  <th className="right">Qté totale</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(line.components ?? []).map((c) => (
                                  <tr key={c.id}>
                                    <td>{c.nameSnapshot}</td>
                                    <td className="mono" style={{ fontSize: 11.5 }}>
                                      {c.skuSnapshot}
                                    </td>
                                    <td className="right tn">{c.unitQuantity}</td>
                                    <td className="right tn">{c.totalQuantity}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div
              style={{
                padding: '14px 18px',
                borderTop: '1px solid var(--a-border)',
                background: 'var(--a-surface-2)',
              }}
            >
              <div style={{ maxWidth: 340, marginLeft: 'auto' }}>
                <div className="info-row">
                  <span className="l">Sous-total</span>
                  <span className="v tn">{fmtFCFA(order.subtotalAmount)}</span>
                </div>
                <div className="info-row">
                  <span className="l">Livraison · {DELIVERY_MODE_LABEL[order.deliveryMode]}</span>
                  <span className="v tn">{fmtFCFA(order.shippingAmount)}</span>
                </div>
                {order.discountAmount > 0 && (
                  <div className="info-row">
                    <span className="l">Remise</span>
                    <span className="v tn" style={{ color: 'var(--a-success)' }}>
                      −{fmtFCFA(order.discountAmount)}
                    </span>
                  </div>
                )}
                {order.taxAmount > 0 && (
                  <div className="info-row">
                    <span className="l">TVA</span>
                    <span className="v tn">{fmtFCFA(order.taxAmount)}</span>
                  </div>
                )}
                <div
                  className="info-row"
                  style={{
                    borderTop: '1px solid var(--a-border-strong)',
                    paddingTop: 10,
                    marginTop: 4,
                    borderBottom: 'none',
                  }}
                >
                  <span className="l" style={{ fontWeight: 700, color: 'var(--a-ink)' }}>
                    Total {order.paidAt ? 'payé' : 'à payer'}
                  </span>
                  <span className="v tn">
                    <strong
                      style={{
                        fontFamily: 'var(--a-font-display)',
                        fontSize: 18,
                      }}
                    >
                      {fmtFCFA(order.totalAmount)}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Timeline temps réel (workflow visuel) */}
          <Card>
            <CardHead
              title="Suivi temps réel"
              sub={`${WORKFLOW_STEPS.length} étapes${isTerminal ? '' : ' · actuel : ' + ORDER_STATUS_SUB_LABEL[order.status]}`}
              right={
                !isTerminal && (
                  <Tag variant="info" noDot>
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: 'currentColor',
                        display: 'inline-block',
                        marginRight: 6,
                        animation: 'pulse 1.6s infinite',
                      }}
                    />
                    Live
                  </Tag>
                )
              }
            />
            <CardBody>
              {isTerminal ? (
                <div
                  style={{
                    padding: '12px 14px',
                    background: 'var(--a-surface-2)',
                    border: '1px solid var(--a-border)',
                    borderRadius: 8,
                    fontSize: 13,
                  }}
                >
                  <strong>{s.l}</strong>
                  <div className="muted" style={{ marginTop: 4 }}>
                    {order.status === 'CANCELLED' && order.cancelledAt
                      ? `Annulée le ${new Date(order.cancelledAt).toLocaleString('fr-FR')}`
                      : order.status === 'REFUNDED'
                        ? 'Commande remboursée, cycle terminé.'
                        : 'Commande en litige — voir historique pour le détail.'}
                  </div>
                </div>
              ) : (
                <div className="timeline-l">
                  {WORKFLOW_STEPS.map((step, i) => {
                    const state = getStepState(i, currentStepIdx);
                    const ts =
                      state === 'done' && order.paidAt && step.status === 'PAID'
                        ? new Date(order.paidAt).toLocaleString('fr-FR')
                        : state === 'done' && order.deliveredAt && step.status === 'DELIVERED'
                          ? new Date(order.deliveredAt).toLocaleString('fr-FR')
                          : state === 'done' && order.completedAt && step.status === 'COMPLETED'
                            ? new Date(order.completedAt).toLocaleString('fr-FR')
                            : state === 'done'
                              ? 'Terminé'
                              : state === 'active'
                                ? 'En cours…'
                                : 'À venir';
                    return (
                      <div key={step.status} className={`ev ${state}`}>
                        <div className="t">{step.label}</div>
                        <div className="ts">{ts}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Journal technique (collapsible) */}
          <Card>
            <CardHead
              title="Journal technique"
              sub={`${pluriel((events ?? []).length, 'événement')} au journal`}
              right={
                <AdminButton variant="ghost" onClick={() => setShowJournal((v) => !v)}>
                  {showJournal ? 'Masquer' : 'Afficher'}
                </AdminButton>
              }
            />
            {showJournal && (
              <CardBody>
                {(events ?? []).length === 0 ? (
                  <div className="empty">Aucun événement enregistré.</div>
                ) : (
                  <ol
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'grid',
                      gap: 10,
                    }}
                  >
                    {(events ?? []).map((ev) => {
                      const fromLabel = ev.fromStatus
                        ? statusOrderV2(ev.fromStatus as OrderStatus).l
                        : null;
                      const toLabel = ev.toStatus
                        ? statusOrderV2(ev.toStatus as OrderStatus).l
                        : null;
                      const reasonLabel = ev.reasonCode ? resolveReasonLabel(ev.reasonCode) : null;
                      const color = EVENT_COLOR_CSS[orderEventColor(ev.type)];
                      // Les envois au client (SMS, WhatsApp) ne laissaient
                      // aucune trace consultable : impossible de savoir si
                      // quelqu'un avait été prévenu, ni pourquoi non. On ne
                      // montre que ce que l'événement porte réellement —
                      // afficher des cases vides laisserait croire à un
                      // envoi qui n'a pas eu lieu.
                      const notif = readNotificationDetails(ev.payload);
                      return (
                        <li
                          key={ev.id}
                          style={{
                            position: 'relative',
                            borderLeft: `3px solid ${color}`,
                            paddingLeft: 14,
                          }}
                        >
                          <span
                            aria-hidden
                            style={{
                              position: 'absolute',
                              left: -7,
                              top: 4,
                              width: 11,
                              height: 11,
                              borderRadius: '50%',
                              background: color,
                              border: '2px solid var(--a-surface, #fff)',
                            }}
                          />
                          <div
                            className="mono"
                            style={{ fontSize: 11, color: 'var(--a-ink-soft)' }}
                          >
                            {new Date(ev.createdAt).toLocaleString('fr-FR')}
                          </div>
                          <div style={{ marginTop: 2 }}>
                            <strong>{orderEventLabel(ev.type)}</strong>
                            {fromLabel && toLabel && (
                              <span className="muted">
                                {' '}
                                · {fromLabel} → {toLabel}
                              </span>
                            )}
                            {reasonLabel && (
                              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                Motif : {reasonLabel}
                              </div>
                            )}
                            {hasNotificationDetails(notif) && (
                              <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                                {[
                                  notif.channel
                                    ? (NOTIFICATION_CHANNEL_LABEL[notif.channel] ?? notif.channel)
                                    : null,
                                  notif.template,
                                  notif.recipient,
                                  notif.result
                                    ? (NOTIFICATION_RESULT_LABEL[notif.result] ?? notif.result)
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </CardBody>
            )}
          </Card>

          {/* Notes internes */}
          <Card>
            <CardHead title="Notes internes" sub="Visibles uniquement par l'équipe Akwaba" />
            <CardBody>
              {(notes ?? []).length === 0 ? (
                <div className="empty">Aucune note interne.</div>
              ) : (
                <div>
                  {(notes ?? []).map((n, i, arr) => (
                    <div
                      key={n.id}
                      style={{
                        display: 'flex',
                        gap: 10,
                        padding: '10px 0',
                        borderBottom: i < arr.length - 1 ? '1px solid var(--a-border)' : 'none',
                      }}
                    >
                      <AdminAvatar name="Akwaba" />
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 2,
                          }}
                        >
                          <strong style={{ fontSize: 12 }}>Note interne</strong>
                          <span className="muted" style={{ fontSize: 11 }}>
                            {new Date(n.createdAt).toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--a-ink-2)' }}>{n.body}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <textarea
                className="aform-i"
                rows={2}
                placeholder="Ajouter une note interne..."
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                style={{ marginTop: 12 }}
                data-testid="note-input"
              />
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: 8,
                }}
              >
                <AdminButton variant="primary" onClick={onAddNote} disabled={!noteDraft.trim()}>
                  Ajouter la note
                </AdminButton>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* SIDE */}
        <div className="detail-side">
          {/* Client */}
          <Card>
            <CardHead
              title="Client"
              right={
                order.customer && (
                  <AdminButton
                    variant="ghost"
                    onClick={() => navigate(`/clients/${order.customer!.id}`)}
                  >
                    Profil <AdminIcon n="chevR" s={12} />
                  </AdminButton>
                )
              }
            />
            <CardBody>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <AdminAvatar name={customerName} size="lg" />
                <div style={{ minWidth: 0 }}>
                  <strong>{customerName}</strong>
                  <div
                    style={{
                      marginTop: 3,
                      display: 'flex',
                      gap: 6,
                      flexWrap: 'wrap',
                    }}
                  >
                    {/*
										  Un opérateur au téléphone doit savoir à qui il parle :
										  un particulier, une société — dont la facture exige
										  d'autres mentions — ou quelqu'un qui n'a pas de compte
										  et ne peut donc pas consulter son historique.
										*/}
                    <Tag
                      variant={
                        order.customer?.status === 'guest'
                          ? 'ochre'
                          : order.customer?.type === 'b2b'
                            ? 'info'
                            : 'neutral'
                      }
                      noDot
                    >
                      {customerKindLabel(order.customer)}
                    </Tag>
                    {order.flags.vip && (
                      <Tag variant="terra" noDot>
                        VIP
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
              {order.customer?.type === 'b2b' && (
                <>
                  <div className="info-row">
                    <span className="l">Raison sociale</span>
                    <span className="v">{order.customer.displayName ?? '—'}</span>
                  </div>
                  {/*
									  Ces numéros se recopiaient à la main dans les factures et
									  les relances — une erreur de saisie, et le document repart.
									*/}
                  {order.customer.niu && (
                    <IdentifiantCopiable label="NUI" valeur={order.customer.niu} />
                  )}
                  {order.customer.rccm && (
                    <IdentifiantCopiable label="RCCM" valeur={order.customer.rccm} />
                  )}
                </>
              )}
              <div className="info-row">
                <span className="l">Téléphone</span>
                <span className="v mono">{order.customer?.phone ?? '—'}</span>
              </div>
              <div className="info-row">
                <span className="l">Email</span>
                <span className="v mono" style={{ fontSize: 11 }}>
                  {order.customer?.email ?? '—'}
                </span>
              </div>
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <span className="l">Source commande</span>
                <span className="v">{ORDER_SOURCE_LABEL[order.source]}</span>
              </div>
            </CardBody>
          </Card>

          {/* Livraison */}
          <Card>
            <CardHead title="Livraison" />
            <CardBody>
              {order.shippingAddress ? (
                <>
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'flex-start',
                      marginBottom: 12,
                    }}
                  >
                    <AdminIcon n="pin" s={16} c="var(--a-terracotta)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ fontSize: 13 }}>
                        {order.shippingAddress.city}
                        {order.shippingAddress.district && ` · ${order.shippingAddress.district}`}
                      </strong>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--a-ink-soft)',
                          marginTop: 2,
                        }}
                      >
                        {[
                          order.shippingAddress.recipientName,
                          order.shippingAddress.streetLine,
                          order.shippingAddress.region,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                  </div>
                  {order.shippingAddress.instructions && (
                    <div
                      style={{
                        padding: 10,
                        background: 'var(--a-cream)',
                        borderRadius: 6,
                        fontSize: 11.5,
                        color: 'var(--a-ink-2)',
                        fontStyle: 'italic',
                        marginBottom: 12,
                      }}
                    >
                      « {order.shippingAddress.instructions} »
                    </div>
                  )}
                  {order.shippingAddress.landmark && (
                    <div className="info-row">
                      <span className="l">Repère</span>
                      <span className="v">{order.shippingAddress.landmark}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty" style={{ marginBottom: 12 }}>
                  Aucune adresse renseignée.
                </div>
              )}
              <div className="info-row">
                <span className="l">Méthode</span>
                <span className="v">{DELIVERY_MODE_LABEL[order.deliveryMode]}</span>
              </div>
              <div className="info-row">
                <span className="l">Frais</span>
                <span className="v tn">{fmtFCFA(order.shippingAmount)}</span>
              </div>
              {typeof order.metadata?.deliveryZoneLabel === 'string' && (
                <div className="info-row">
                  <span className="l">Zone</span>
                  <span className="v">{order.metadata.deliveryZoneLabel as string}</span>
                </div>
              )}
              <div className="info-row">
                <span className="l">Hub</span>
                <span className="v">
                  {order.hub ? (
                    <>
                      <strong>{order.hub.name}</strong>
                    </>
                  ) : (
                    <Tag variant="warning" noDot>
                      Non assigné
                    </Tag>
                  )}
                </span>
              </div>
              {order.deliveryPromisedAt && !order.deliveredAt && (
                <div className="info-row">
                  <span className="l">Livraison prévue</span>
                  <span className="v" style={{ color: 'var(--a-terra-deep)', fontWeight: 600 }}>
                    {new Date(order.deliveryPromisedAt).toLocaleDateString('fr-FR', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                    })}
                    {typeof order.metadata?.deliveryEtaHours === 'number' && (
                      <span className="muted" style={{ fontWeight: 400 }}>
                        {' '}
                        · sous {order.metadata.deliveryEtaHours as number} h
                      </span>
                    )}
                  </span>
                </div>
              )}
              {order.deliveredAt && (
                <div className="info-row">
                  <span className="l">Livrée le</span>
                  <span className="v">{new Date(order.deliveredAt).toLocaleString('fr-FR')}</span>
                </div>
              )}
              {operatorName && (
                <div className="info-row" style={{ borderBottom: 'none' }}>
                  <span className="l">Opérateur</span>
                  <span className="v">
                    <button
                      type="button"
                      onClick={() =>
                        operator && navigate(`/administration/utilisateurs/${operator.id}`)
                      }
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: 'var(--a-terracotta)',
                        cursor: 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {operatorName}
                    </button>
                  </span>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Paiement */}
          <Card>
            <CardHead title="Paiement" />
            <CardBody>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                {order.paymentMethod ? (
                  <>
                    <PaymentMethodBadge method={order.paymentMethod} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <strong style={{ fontSize: 13, display: 'block' }}>
                        {PAYMENT_METHOD_LABEL[order.paymentMethod]}
                      </strong>
                      {order.paymentReference && (
                        <div className="muted mono" style={{ fontSize: 11 }}>
                          {order.paymentReference.slice(0, 16)}…
                        </div>
                      )}
                    </div>
                    <Tag variant={PAYMENT_STATUS_CLS[order.paymentStatus] ?? 'neutral'}>
                      {PAYMENT_STATUS_LABEL[order.paymentStatus]}
                    </Tag>
                  </>
                ) : (
                  <div className="muted">Méthode non définie</div>
                )}
              </div>
              {order.paymentReference && (
                <div className="info-row">
                  <span className="l">Référence</span>
                  <span className="v mono" style={{ fontSize: 10 }}>
                    {order.paymentReference}
                  </span>
                </div>
              )}
              {order.paidAt && (
                <div className="info-row">
                  <span className="l">Payée le</span>
                  <span className="v" style={{ fontSize: 11 }}>
                    {new Date(order.paidAt).toLocaleString('fr-FR')}
                  </span>
                </div>
              )}
              <div className="info-row">
                <span className="l">Devise</span>
                <span className="v mono">{order.currency}</span>
              </div>

              {/*
							  Virement : tout ce qu'il faut pour rapprocher un montant reçu
							  d'une commande, et agir sans changer d'écran. L'échéance est en
							  tête parce que c'est elle qui commande l'urgence — passé le
							  terme, la commande s'annule et le stock repart à la vente.
							*/}
              {order.paymentMethod === 'BANK_TRANSFER' && order.paymentStatus !== 'CAPTURED' && (
                <>
                  <div className="info-row">
                    <span className="l">Réservation</span>
                    <span className="v">
                      <Tag variant={etatReservation(order.reservationExpiresAt).tone} noDot>
                        {etatReservation(order.reservationExpiresAt).label}
                      </Tag>
                    </span>
                  </div>
                  {(order.reservationExtensions ?? 0) > 0 && (
                    <div className="info-row">
                      <span className="l">Prolongations</span>
                      <span className="v">
                        {order.reservationExtensions}
                        {estTropProlongee(order.reservationExtensions) && (
                          <span
                            style={{
                              marginLeft: 6,
                              color: 'var(--a-warning)',
                              fontSize: 11,
                            }}
                          >
                            — à revoir
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {order.transferProofUrl && (
                    <div className="info-row">
                      <span className="l">Justificatif</span>
                      <span className="v">
                        <a href={order.transferProofUrl} target="_blank" rel="noopener noreferrer">
                          Ouvrir
                        </a>
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      gap: 6,
                      marginTop: 12,
                      flexWrap: 'wrap',
                    }}
                  >
                    <AdminButton variant="primary" onClick={() => setVirementModal('confirmer')}>
                      Confirmer le virement
                    </AdminButton>
                    <AdminButton variant="ghost" onClick={() => setVirementModal('prolonger')}>
                      Prolonger
                    </AdminButton>
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          {virementModal === 'confirmer' && (
            <ConfirmBankTransferModal order={order} onClose={() => setVirementModal(null)} />
          )}
          {virementModal === 'prolonger' && (
            <ExtendReservationModal order={order} onClose={() => setVirementModal(null)} />
          )}
          {annulationOuverte && (
            <CancelOrderModal
              order={order}
              onClose={() => setAnnulationOuverte(false)}
              onConfirm={onCancel}
            />
          )}

          {/* Actions rapides */}
          <Card>
            <CardHead title="Actions rapides" />
            <CardBody style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/*
							  La tournée se compose depuis l'écran logistique, qui voit tous
							  les colis du jour : l'API n'expose aucune affectation depuis une
							  commande isolée, et affecter à l'aveugle ferait des tournées
							  incohérentes. Le bouton y conduit au lieu d'annoncer, comme
							  avant, une fonction « à venir » qui ne venait pas.
							*/}
              <AdminButton
                variant="ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/logistique')}
              >
                <AdminIcon n="truck" s={14} />
                Ouvrir la logistique
              </AdminButton>
              <AdminButton
                variant="ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate(`/commandes/${order.id}/modifier`)}
              >
                <AdminIcon n="edit" s={14} />
                Modifier les articles
              </AdminButton>
              <AdminButton
                variant="ghost"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => navigate('/paiements?orderId=' + order.id)}
              >
                <AdminIcon n="refresh" s={14} />
                Voir paiements / Rembourser
              </AdminButton>
              {!isTerminal && order.status !== 'COMPLETED' && (
                <AdminButton
                  variant="ghost"
                  style={{
                    justifyContent: 'flex-start',
                    color: 'var(--a-danger)',
                  }}
                  onClick={() => setAnnulationOuverte(true)}
                  data-testid="cancel-order"
                >
                  <AdminIcon n="alert" s={14} />
                  Annuler la commande
                </AdminButton>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.5; }
          100% { transform: scale(1); opacity: 1; }
        }
        @media (max-width: 1024px) {
          .detail { grid-template-columns: 1fr !important; }
          .page-actions { width: 100%; justify-content: flex-start; }
        }
      `}</style>
    </>
  );
}
